import { Client, PortfolioBreakdown } from "creso-core/types";
import { buildClientProductBreakdownItems } from "creso-core/utils";
import { useMemo } from "react";
import { useDistributorProfile } from "creso-core/hooks";
import { ProductCard, type ProductBreakdownItem } from "web-core";
import { GiftTrigger } from "@/modules/gift/components/gift-trigger";
import { TransactionTrigger } from "@core/components/transaction-trigger";
import { OverviewSection } from "./overview-section";

export type { ProductBreakdownItem };

type ProductBreakdownProps = {
  products: ProductBreakdownItem[];
  label?: string;
  defaultOpen?: boolean;
  minProducts?: number;
  client?: Client;
};

export function OverviewProductBreakdown({
  client,
  breakdown,
}: {
  client: Client;
  breakdown: PortfolioBreakdown | null;
}) {
  const { data: profile } = useDistributorProfile();

  const products = useMemo(
    () =>
      buildClientProductBreakdownItems(breakdown, profile).map((p) => {
        if (p.key === "GIFT") {
          return {
            ...p,
            action: <GiftTrigger key={p.key} client={client} />,
          };
        }
        return {
          ...p,
          action: (
            <TransactionTrigger
              label="Transact"
              key={p.key}
              clientId={client.id}
            />
          ),
        };
      }),
    [breakdown, profile, client]
  );

  return (
    <ProductBreakdown client={client} products={products} minProducts={2} />
  );
}

export function ProductBreakdown({
  products,
  label = "Products",
  defaultOpen = true,
  minProducts = 1,
}: ProductBreakdownProps) {
  if (products.length < minProducts) return null;

  return (
    <OverviewSection defaultOpen={defaultOpen} label={label}>
      <div className="flex flex-row items-center gap-4">
        {products.map((product) => (
          <ProductCard key={product.key} product={product}>
            {product.action ?? null}
          </ProductCard>
        ))}
      </div>
    </OverviewSection>
  );
}
